import { A } from '../router';
import { SITE, usePageMeta } from '../siteMeta';
import { SiteNav } from './SiteChrome';

/** Standalone site pages: FAQ, About, Press kit, Updates, 404. */

export function PageShell({ suffix, children }: { suffix: string; children: React.ReactNode }) {
  return (
    <div className="wiki">
      <SiteNav suffix={suffix} />
      <div className="wiki-body" style={{ display: 'block' }}>
        <article className="wiki-content panel page-article">{children}</article>
      </div>
    </div>
  );
}

// ---- FAQ ----------------------------------------------------------------

interface Faq {
  q: string;
  /** Plain-text answer for FAQPage structured data. */
  a: string;
  /** Rich answer for the page itself; falls back to the plain text. */
  body?: React.ReactNode;
}

const FAQS: Faq[] = [
  {
    q: 'What is Big Mech Energy Idle?',
    a: 'A free browser idle game. Your mech auto-fights the machine uprising while you salvage parts and wire them into its power graph. Beat 11 AI Overlords, ascend through 12 ever-bigger war-frames and eventually take the sun back.',
    body: (
      <p>
        A free browser idle game. Your mech auto-fights the machine uprising while you salvage parts and wire them
        into its power graph, a system we call <A to="/wiki/mechmaxxing">MechMaxxing</A>. Beat 11 <A to="/wiki/overlords">AI Overlords</A>,
        ascend through 12 ever-bigger war-frames and eventually take the sun back.
      </p>
    ),
  },
  {
    q: 'Is it really free?',
    a: 'Yes. The web version you are playing now is free: no price, no ads, no account, no microtransactions and nothing to download. Open the page and play.',
  },
  {
    q: 'Does the game progress while I am away?',
    a: 'Yes. Offline progress accrues while the tab is closed, up to a cap, and drone sorties keep running and pay out when you return.',
    body: (
      <p>
        Yes. Offline progress accrues while the tab is closed, up to a cap, and <A to="/wiki/drones">drone sorties</A> keep
        running and pay out when you return.
      </p>
    ),
  },
  {
    q: 'Where is my save stored?',
    a: 'In your browser. The game autosaves every 15 seconds to localStorage and nothing leaves your machine. Export a save code from Settings for a portable backup.',
    body: (
      <p>
        In your browser. The game autosaves every 15 seconds to localStorage and nothing leaves your machine. Export a
        save code from Settings for a portable backup; the <A to="/wiki/saving">Saving guide</A> covers the details and
        the code format.
      </p>
    ),
  },
  {
    q: 'Can I play it on my phone?',
    a: 'It runs in any modern browser, but it is a desktop-first game. The MechMaxxing board likes a big screen and a mouse.',
  },
  {
    q: 'What other games is it like?',
    a: 'If you enjoy incremental games with prestige loops and build-crafting, like Cookie Clicker, Melvor Idle, NGU Idle or Leaf Blower Revolution, this is that genre with a wireable damage pipeline and a lot more mech.',
  },
  {
    q: 'How long does it take to finish?',
    a: 'Your first overlord falls in the first session. The full ladder of 11 overlords and 12 mechs is weeks of mostly idle progression, tuned so each unlock changes how you play.',
  },
  {
    q: 'Is there multiplayer?',
    a: 'No. It is single-player and fully deterministic, running entirely on your machine. No server, no leaderboard, no waiting on anyone.',
  },
  {
    q: 'Will it be on Steam?',
    a: 'Maybe. If there is enough demand, a Steam build is possible. Whether that version would be free to play or paid is still undecided. The web version stays free either way. Progress notes land on the Updates page and in the Discord.',
    body: (
      <p>
        Maybe. If there is enough demand, a Steam build is possible. Whether that version would be free to play or paid
        is still undecided, but the web version you are playing now stays free either way. Progress notes land on
        the <A to="/updates">Updates page</A> and in the <a href={SITE.discordUrl} target="_blank" rel="noreferrer">Discord</a>.
      </p>
    ),
  },
  {
    q: 'Can I build fan tools, planners or save editors?',
    a: 'Please do. The save code format is documented in the wiki, including the exact compression recipe and schema pointers.',
    body: (
      <p>
        Please do. The save code format is documented in the <A to="/wiki/saving">Saving guide</A>, including the exact
        compression recipe and schema pointers. Show off what you build in
        the <a href={SITE.discordUrl} target="_blank" rel="noreferrer">Discord</a>.
      </p>
    ),
  },
  {
    q: 'How do I report a bug or suggest something?',
    a: 'Join the Discord and post it. The developer reads everything.',
    body: (
      <p>
        Join the <a href={SITE.discordUrl} target="_blank" rel="noreferrer">Discord</a> and post it. The developer
        reads everything.
      </p>
    ),
  },
  {
    q: 'Who made this?',
    a: 'Big Mech Energy Idle is built by lifeingray, a solo developer. The About page has the full story.',
    body: (
      <p>
        Big Mech Energy Idle is built by {SITE.devHandle}, a solo developer. The <A to="/about">About page</A> has the
        full story.
      </p>
    ),
  },
];

export function FaqPage() {
  usePageMeta({
    path: '/faq',
    title: `FAQ | ${SITE.name}`,
    description:
      'Answers about Big Mech Energy Idle: is it free, does it progress offline, where saves live, phone support, Steam plans and how to reach the developer.',
    markdownPath: '/faq.md',
    jsonLd: [
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: FAQS.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
    ],
  });
  return (
    <PageShell suffix="FAQ">
      <h1 className="wiki-h1">Frequently asked questions</h1>
      {FAQS.map((f) => (
        <section key={f.q} className="faq-item">
          <h2 className="faq-q">{f.q}</h2>
          {f.body ?? <p>{f.a}</p>}
        </section>
      ))}
      <p className="faq-more">
        Something else? Ask in the <a href={SITE.discordUrl} target="_blank" rel="noreferrer">Discord</a> or dig
        through the <A to="/wiki">wiki</A>.
      </p>
    </PageShell>
  );
}

// ---- About ---------------------------------------------------------------

export function AboutPage() {
  usePageMeta({
    path: '/about',
    title: `About | ${SITE.name}`,
    description:
      'Who makes Big Mech Energy Idle, how it is built, what it does with your data (nothing) and where to find the developer.',
    markdownPath: '/about.md',
  });
  return (
    <PageShell suffix="About">
      <h1 className="wiki-h1">About Big Mech Energy Idle</h1>
      <p>
        Big Mech Energy Idle is a darkly comedic idle game about the last loyal AI on Earth, built
        by <strong>{SITE.devHandle}</strong>, a solo developer with a fondness for big robots and bigger numbers.
      </p>
      <h2>The design promise</h2>
      <ul>
        <li><strong>Idle first.</strong> The game fights and progresses on its own. Active play optimizes; it never babysits.</li>
        <li><strong>No dark patterns.</strong> No energy timers, no daily streaks, no fear of missing out. Come back whenever; the mech kept working.</li>
        <li><strong>Free means free, on the web.</strong> The browser version has no ads, no account and no purchases: nothing to buy, nothing to refund. A future desktop or Steam build is a separate thing and could be priced, but the web game stays free.</li>
        <li><strong>Numbers you can read.</strong> Every multiplier shows its work. Hover an attack and see the whole formula, <A to="/wiki/reading-the-math">documented in the wiki</A>.</li>
      </ul>
      <h2>How it is built</h2>
      <p>
        A deterministic TypeScript core drives the whole simulation; the browser only renders it. The art is
        procedural PixiJS, drawn from code with no asset files. Big numbers run on break_infinity, saves are
        versioned and migrate forward forever, and the <A to="/wiki/saving">save format is documented</A> so fans can
        build planners and tools.
      </p>
      <h2>Your data</h2>
      <p>
        There is no server. Your save lives in your browser's localStorage and never leaves your machine. Export a
        save code any time; it is yours.
      </p>
      <h2>Say hello</h2>
      <p>
        The community lives in the <a href={SITE.discordUrl} target="_blank" rel="noreferrer">Discord</a>: bug
        reports, build screenshots, balance arguments and the occasional pour celebration. The developer is in there
        every day.
      </p>
    </PageShell>
  );
}

// ---- Press kit -------------------------------------------------------------

const PRESS_SHOTS = [
  { src: '/media/shot-battle.png', alt: 'A high-tier mech fights a wave of rogue machines with pets at heel' },
  { src: '/media/shot-battle2.png', alt: 'A late-game mech holding the line deep in the campaign' },
  { src: '/media/shot-mechmaxxing.png', alt: 'The MechMaxxing board: parts wired into a power graph' },
  { src: '/media/shot-overlord.png', alt: 'An overlord duel in a themed arena' },
  { src: '/media/shot-mechs.png', alt: 'The mech gallery showing the twelve incarnations' },
];

export function PressPage() {
  usePageMeta({
    path: '/press',
    title: `Press Kit | ${SITE.name}`,
    description:
      'Press kit for Big Mech Energy Idle: factsheet, descriptions, feature list and downloadable screenshots.',
  });
  return (
    <PageShell suffix="Press">
      <h1 className="wiki-h1">Press kit</h1>
      <h2>Factsheet</h2>
      <ul>
        <li><strong>Game:</strong> {SITE.name}</li>
        <li><strong>Developer:</strong> {SITE.devHandle} (solo)</li>
        <li><strong>Status:</strong> in active development, playable now</li>
        <li><strong>Platforms:</strong> web browser; a desktop build is planned</li>
        <li><strong>Price:</strong> free, no ads, no purchases</li>
        <li><strong>Engine:</strong> custom deterministic TypeScript core with procedural PixiJS art</li>
        <li><strong>Contact:</strong> <a href={SITE.discordUrl} target="_blank" rel="noreferrer">Discord</a></li>
      </ul>
      <h2>One liner</h2>
      <p>{SITE.tagline}</p>
      <h2>Description</h2>
      <p>
        Humanity's machines went feral; one defense AI stayed loyal. That one is you. Big Mech Energy Idle is an
        incremental game where your mech auto-battles the uprising while you wire salvaged parts into its power
        graph, duel the 11 AI Overlords running the rebellion and upload yourself into 12 increasingly absurd
        war-frames, from a junk gremlin to a machine with a star for a heart. Each fallen overlord surrenders a whole
        subsystem: drone sorties, a pet kennel, a forge, a rail network, a stock market, a city that moves in on your
        legs. It plays itself when you are gone and rewards you for showing up.
      </p>
      <h2>Features</h2>
      <ul>
        <li>MechMaxxing: a visible, wireable damage pipeline built from looted parts</li>
        <li>12 mech incarnations, each a transformation with its own silhouette</li>
        <li>11 overlord duels, each unlocking a full subsystem</li>
        <li>Pets: shrunken enemies that follow you into battle, plus gold trophy minis</li>
        <li>True idle: offline progress, drone sorties and a game that respects your absence</li>
        <li>All procedural art, deterministic simulation, saves that migrate forever</li>
      </ul>
      <h2>Screenshots</h2>
      <p>Right-click and save, or grab them from the site. Use them freely in coverage.</p>
      <div className="press-shots">
        {PRESS_SHOTS.map((s) => (
          <a key={s.src} href={s.src} target="_blank" rel="noreferrer">
            <img src={s.src} alt={s.alt} loading="lazy" />
          </a>
        ))}
      </div>
    </PageShell>
  );
}

// ---- Updates / devlog -------------------------------------------------------

interface UpdateEntry { id: string; date: string; title: string; body: React.ReactNode; }

const UPDATES: UpdateEntry[] = [
  {
    id: 'reboot-endless-loop',
    date: '2026-06-13',
    title: 'The ladder never ends now: Reboot',
    body: (
      <p>
        The final chassis used to be a dead end: no bigger frame meant no more Cores. Now Ascension becomes
        <strong> Reboot</strong>, reset the run for Cores while you keep the Dyson-Frame and everything else. It is gated
        by <A to="/wiki/star-harvest">Star Harvest</A>: each satellite you launch buys one reboot, and a finished swarm
        lets you reboot whenever. Full details in the <A to="/wiki/ascension">Ascension guide</A>.
      </p>
    ),
  },
  {
    id: 'drop-rate-retune',
    date: '2026-06-13',
    title: 'Drops got rarer where it counts',
    body: (
      <p>
        Pets now start at 1 in 1,000 per kill and halve with every copy you own, so a full bench is a long game and the
        <A to="/wiki/training-yard"> Training Yard</A> earns its keep. Part tiers were pulled back at the top too: Nerfed
        stops dropping once a run is fully ramped, Based caps near 5 percent, and Lit is the new sweet spot. The
        <A to="/wiki/pets"> Pets</A> and <A to="/wiki/parts">Parts</A> guides spell out the exact curves.
      </p>
    ),
  },
  {
    id: 'mobile-and-home-polish',
    date: '2026-06-13',
    title: 'Better on a phone',
    body: (
      <p>
        Site menus now collapse into a hamburger on narrow screens, the wiki reads full width on mobile with its section
        list a tap away, and the play screen footer sits where it belongs. The home page got a tidy-up, and the title
        picked up a little static.
      </p>
    ),
  },
  {
    id: 'protect-parts-and-wave-hold',
    date: '2026-06-13',
    title: 'Lock your favorite parts, farm any wave',
    body: (
      <p>
        Parts can now be protected with one tap, so scrap, the <A to="/wiki/smelter">Smelter</A> and
        the <A to="/wiki/foundry">Foundry</A> all skip the gear you care about, and their tallies no longer count it.
        The arena also gained a Hold toggle that farms the current wave instead of advancing, and the active attacks,
        now named <strong>Full Send</strong> and <strong>Maximum Effort</strong>, show their charge as fill bars
        instead of shifting countdown text. Settings can copy your save code in one click.
      </p>
    ),
  },
  {
    id: 'mech-glow-up',
    date: '2026-06-13',
    title: 'The mechs got a glow-up',
    body: (
      <p>
        Every war-frame, overlord and wave enemy got an art pass: bigger late-tier silhouettes, juiced weapons and two
        full re-imaginings. The Dyson Frame is now a star-hearted war-angel and Sir Cumference is a roaring coal
        furnace on stubby legs. See the whole ladder on <A to="/mechs">The Mechs</A>.
      </p>
    ),
  },
  {
    id: 'save-codes-for-fan-tools',
    date: '2026-06-12',
    title: 'Save codes are now fan tool friendly',
    body: (
      <p>
        The <A to="/wiki/saving">Saving guide</A> now documents the full save code format: the compression recipe,
        the JSON envelope and how versioned migration works. If you want to build a planner, analyzer or editor, the
        door is open. The backup nudge also behaves better: one click copies a fresh code.
      </p>
    ),
  },
  {
    id: 'unit-and-goated-tiers',
    date: '2026-06-12',
    title: 'The tier ladder goes full meme: Unit and GOATED',
    body: (
      <p>
        Part tiers are now Nerfed, Mid, Lit, Sweaty, Based, Unit and GOATED. Drops stop at Based;
        the top two tiers only come out of the <A to="/wiki/foundry">Foundry</A>, and a GOATED pour is an overnight
        project. The <A to="/wiki/parts">Parts guide</A> has the full multipliers.
      </p>
    ),
  },
  {
    id: 'pets-obedience-school',
    date: '2026-06-12',
    title: 'Pets went to obedience school',
    body: (
      <p>
        Drops and overlord kills now teach a pet up to level 10; everything past that is earned in
        the <A to="/wiki/training-yard">Training Yard</A>, capped by trial: Fetch to 5, Guard to 10, Sit. DOWN. to 15
        and the new final discipline, Get 'Em, to 25. Overlord minis train right alongside the Doombahs, gold and
        disgruntled.
      </p>
    ),
  },
  {
    id: 'duel-countdown',
    date: '2026-06-11',
    title: 'Overlord duels open with a countdown',
    body: (
      <p>
        Challenging an <A to="/wiki/overlords">overlord</A> now holds the arena for three seconds with a gold 3-2-1
        before the fight starts. You get a breath, the boss gets an entrance, and nobody gets sucker punched during a
        tab switch.
      </p>
    ),
  },
  {
    id: 'overlord-minis',
    date: '2026-06-11',
    title: 'Every overlord drops a trophy mini',
    body: (
      <p>
        Defeat any overlord five times and a gold pocket-sized copy joins your bench with its own themed bonus, from
        the Synth CFO to Nightlight. Five more defeats per level. Re-fights finally have a reason, and
        the <A to="/wiki/pets">Pets guide</A> tracks all 11.
      </p>
    ),
  },
];

export function UpdatesPage() {
  usePageMeta({
    path: '/updates',
    title: `Updates | ${SITE.name}`,
    description:
      'Development updates for Big Mech Energy Idle: new systems, balance changes and quality of life, in plain language.',
    markdownPath: '/updates.md',
  });
  return (
    <PageShell suffix="Updates">
      <h1 className="wiki-h1">Updates</h1>
      <p>What changed and why, in plain language. The fine print lives in the <A to="/wiki">wiki</A>.</p>
      {UPDATES.map((u) => (
        <section key={u.id} id={u.id} className="update-entry">
          <h2 className="update-title">{u.title}</h2>
          <div className="update-date">{u.date}</div>
          {u.body}
        </section>
      ))}
    </PageShell>
  );
}

// ---- 404 --------------------------------------------------------------------

export function NotFoundPage() {
  usePageMeta({
    path: '/404',
    title: `Page not found | ${SITE.name}`,
    description: 'That page does not exist.',
    noindex: true,
  });
  return (
    <PageShell suffix="404">
      <h1 className="wiki-h1">Signal lost</h1>
      <p>That page does not exist, or an overlord encrypted it. Try one of these instead:</p>
      <ul>
        <li><A to="/">The home page</A></li>
        <li><A to="/play">Play the game</A></li>
        <li><A to="/wiki">The wiki</A></li>
      </ul>
    </PageShell>
  );
}
